// iter618 — One-click "Open a new branch" wizard.
//
// Asks for just the bare minimum (Name + Country) and infers everything
// else: currency, tax rate, receipt language, bilingual default, print
// mode, and an optional 24h "training mode" so new staff can onboard
// without paper waste. Backend endpoint: POST /api/branches/wizard.

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, GraduationCap, Globe } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const COUNTRY_DEFAULTS_PREVIEW = {
  sa: { name: 'Saudi Arabia (KSA)', currency: 'SAR', vat: 15, lang: 'ar', bilingual: true, second: 'en' },
  ae: { name: 'United Arab Emirates', currency: 'AED', vat: 5, lang: 'ar', bilingual: true, second: 'en' },
  kw: { name: 'Kuwait', currency: 'KWD', vat: 0, lang: 'ar', bilingual: true, second: 'en' },
  bh: { name: 'Bahrain', currency: 'BHD', vat: 10, lang: 'ar', bilingual: true, second: 'en' },
  om: { name: 'Oman', currency: 'OMR', vat: 5, lang: 'ar', bilingual: true, second: 'en' },
  qa: { name: 'Qatar', currency: 'QAR', vat: 0, lang: 'ar', bilingual: true, second: 'en' },
  eg: { name: 'Egypt', currency: 'EGP', vat: 14, lang: 'ar', bilingual: false, second: 'en' },
  gb: { name: 'United Kingdom', currency: 'GBP', vat: 20, lang: 'en', bilingual: false, second: 'ar' },
  us: { name: 'United States', currency: 'USD', vat: 0, lang: 'en', bilingual: false, second: 'es' },
  in: { name: 'India', currency: 'INR', vat: 18, lang: 'en', bilingual: false, second: 'hi' },
  pk: { name: 'Pakistan', currency: 'PKR', vat: 17, lang: 'en', bilingual: false, second: 'ur' },
  ph: { name: 'Philippines', currency: 'PHP', vat: 12, lang: 'en', bilingual: false, second: 'tl' },
};

export default function BranchWizardDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [country, setCountry] = useState('sa');
  const [startTraining, setStartTraining] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const preview = COUNTRY_DEFAULTS_PREVIEW[country] || COUNTRY_DEFAULTS_PREVIEW.sa;

  const reset = () => {
    setName(''); setLocation(''); setCountry('sa'); setStartTraining(true); setSubmitting(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Branch name is required'); return; }
    try {
      setSubmitting(true);
      const { data } = await api.post('/branches/wizard', {
        name: name.trim(),
        location: location.trim() || null,
        country_code: country,
        start_training_mode: startTraining,
      });
      toast.success(`Branch "${data.name}" created with ${preview.currency} defaults${startTraining ? ' · Training mode ON for 24h' : ''}.`);
      onCreated?.(data);
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create branch');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="branch-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-outfit">
            <Sparkles size={18} className="text-amber-500" /> Open a new branch
          </DialogTitle>
          <p className="text-[12px] text-muted-foreground">
            Just the essentials — we&apos;ll pre-fill currency, tax, language, and print defaults so you can start selling in 30 seconds.
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="bwd-name">Branch name *</Label>
            <Input
              id="bwd-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Riyadh Downtown"
              autoFocus
              data-testid="branch-wizard-name"
            />
          </div>
          <div>
            <Label htmlFor="bwd-location">Location <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              id="bwd-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Street, city, postcode"
              data-testid="branch-wizard-location"
            />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Globe size={12} />Country</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="branch-wizard-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(COUNTRY_DEFAULTS_PREVIEW).map(([code, c]) => (
                  <SelectItem key={code} value={code}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Preview card so the manager sees what we'll auto-apply. */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1.5" data-testid="branch-wizard-preview">
            <div className="text-[11px] uppercase font-bold tracking-wider text-emerald-800">We&apos;ll auto-apply</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] text-emerald-900">
              <div><span className="text-emerald-700/80">Currency:</span> <strong>{preview.currency}</strong></div>
              <div><span className="text-emerald-700/80">VAT:</span> <strong>{preview.vat}%</strong></div>
              <div><span className="text-emerald-700/80">Receipts:</span> <strong>{preview.bilingual ? `${preview.lang.toUpperCase()} + ${preview.second.toUpperCase()}` : preview.lang.toUpperCase()}</strong></div>
              <div><span className="text-emerald-700/80">Print mode:</span> <strong>Auto</strong></div>
            </div>
          </div>
          <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-amber-50/50">
            <input
              type="checkbox"
              checked={startTraining}
              onChange={(e) => setStartTraining(e.target.checked)}
              className="mt-0.5 rounded"
              data-testid="branch-wizard-training-toggle"
            />
            <div className="min-w-0">
              <div className="text-[12px] font-semibold flex items-center gap-1.5 text-amber-900">
                <GraduationCap size={12} className="text-amber-600" />
                Start in Training mode (24h)
              </div>
              <div className="text-[10px] text-amber-700/80 leading-tight">
                Every print at this branch opens the OS dialog so new staff see what would print before paper moves. Auto-expires in 24h.
              </div>
            </div>
          </label>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={submitting || !name.trim()} className="rounded-full" data-testid="branch-wizard-submit">
              {submitting ? 'Creating…' : 'Open branch'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
